import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import Heading from "../../../../../../common/element/Heading";
import BtnLight from "../../../../../../common/element/Buttons/btnLight";
import AllProviderMultiSelectDropdown from "../../../../../../common/MultiSelectFilter/AllProviderMultiSelectDropdown";
import ProvideTyperMultiSelectDropdown from "../../../../../../common/MultiSelectFilter/ProvideTyperMultiSelectDropdown";
import ThreeDotBouncing from "../../../../../../common/element/PageLoader";
import { setBreadcrumbs } from "../../../../../../redux/classes/breadcrumbsSlice";
import useUnauthorizedHandler from "../../../../../../common/snippet/useUnauthorizedHandler";
import WeeklyClosuresList from "./WeeklyClosuresList";
import { GenerateWeeklyClosureReport } from "../../../../../../actions/Admin/reports/GenerateAdminWeeklyClosureReport";
import moment from "moment";

const WeeklyClosures = () => {
  const dispatch = useDispatch();
  const handleUnauthorizedAccess = useUnauthorizedHandler();

  const [reportData, setReportData] = useState({ rows: [], total: 0, metadata: {} });
  const [isLoading, setIsLoading] = useState(false);

  const [filters, setFilters] = useState({
    provider_org_ids: [],
    provider_type_ids: [],
  });

  const latestFiltersRef = useRef(filters);
  const hasFetchedInitialRef = useRef(false);

  const normalizedFilters = useMemo(() => {
    const normalizeOptionList = (list = []) =>
      list
        .map((item) =>
          item?.provider_org_id ??
          item?.organisation_id ??
          item?.provider_type_id ??
          item?.value ??
          item
        )
        .filter((entry) => entry !== undefined && entry !== null);

    const providers = Array.isArray(filters.provider_org_ids)
      ? normalizeOptionList(filters.provider_org_ids)
      : [];
    const providerTypes = Array.isArray(filters.provider_type_ids)
      ? normalizeOptionList(filters.provider_type_ids)
      : [];

    const base = {
      provider_org_ids: providers,
      provider_type_ids: providerTypes,
    };

    return Object.entries(base).reduce((accumulator, [key, value]) => {
      if (Array.isArray(value) && value.length) {
        accumulator[key] = value;
      }
      return accumulator;
    }, {});
  }, [filters]);

  const fetchWeeklyClosures = useCallback(
    async (activeFilters) => {
      if (!activeFilters) {
        return null;
      }

      setIsLoading(true);
      try {
        const response = await dispatch(GenerateWeeklyClosureReport(activeFilters));

        const successStatuses = new Set(["GETSUCCESS", "SUCCESS", "OK"]);
        if (response?.success === true || successStatuses.has(response?.status)) {
          const payload = response?.data ?? response ?? {};
          const rows = Array.isArray(payload?.rows)
            ? payload.rows
            : Array.isArray(payload?.data?.rows)
            ? payload.data.rows
            : [];
          const metadataCandidate = payload?.metadata ?? payload?.data?.metadata ?? {};
          const metadata = metadataCandidate && typeof metadataCandidate === "object"
            ? metadataCandidate
            : {};
          const total = Number(metadata?.total ?? rows.length) || rows.length;

          setReportData({ rows, total, metadata });
        } else if (response?.status === 401) {
          handleUnauthorizedAccess();
          setReportData({ rows: [], total: 0, metadata: {} });
        } else {
          console.warn("Weekly Closure report returned unexpected response", response);
          setReportData({ rows: [], total: 0, metadata: {} });
        }

        return response;
      } catch (error) {
        console.error("Error fetching Weekly Closure report:", error);
        setReportData({ rows: [], total: 0, metadata: {} });
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, handleUnauthorizedAccess]
  );

  useEffect(() => {
    dispatch(
      setBreadcrumbs([
        {
          label: "Reports",
          url: "/admin/reports",
          home: "/admin/dashboard",
        },
        {
          label: "Weekly closures",
          url: "/admin/reports/weekly-closures",
        },
      ])
    );
  }, [dispatch]);

  useEffect(() => {
    if (!hasFetchedInitialRef.current) {
      hasFetchedInitialRef.current = true;
      latestFiltersRef.current = normalizedFilters;
      fetchWeeklyClosures(normalizedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const nextFilters = JSON.stringify(normalizedFilters);
    if (JSON.stringify(latestFiltersRef.current) === nextFilters) {
      return;
    }
    latestFiltersRef.current = JSON.parse(nextFilters);
    fetchWeeklyClosures(latestFiltersRef.current);
  }, [normalizedFilters, fetchWeeklyClosures]);

  const updateFilters = useCallback((field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const refreshReport = () => {
    fetchWeeklyClosures(latestFiltersRef.current);
  };

  const hasResults = Array.isArray(reportData?.rows) && reportData.rows.length > 0;

  const metadata = reportData?.metadata ?? {};
  const weekStart = metadata?.week_start
    ? moment(metadata.week_start).format("DD MMM YYYY")
    : null;
  const weekEnd = metadata?.week_end
    ? moment(metadata.week_end).format("DD MMM YYYY")
    : null;

  return (
    <div className="px-5 py-6 lg:px-10 lg:py-8">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <Heading
          title="Weekly closures"
          subtitle="Providers that became inactive in the past 7 days"
        />
        <div className="flex gap-3">
          <BtnLight className="min-w-[120px]" onClick={refreshReport} disabled={isLoading}>
            Refresh
          </BtnLight>
        </div>
      </div>

      {metadata?.total != null && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm text-gray-500">Total closures</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">
              {metadata.total}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm text-gray-500">Week start</p>
            <p className="mt-1 text-base font-medium text-gray-900">
              {weekStart ?? "-"}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm text-gray-500">Week end</p>
            <p className="mt-1 text-base font-medium text-gray-900">
              {weekEnd ?? "-"}
            </p>
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-4 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-gray-700">Provider</span>
            <AllProviderMultiSelectDropdown
              title="Provider"
              updateFilter={(field, value) => updateFilters(field, value)}
              Path="provider"
              fieldName="provider_org_ids"
            />
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-gray-700">Provider type</span>
            <ProvideTyperMultiSelectDropdown
              title="Provider type"
              updateFilter={(field, value) => updateFilters(field, value)}
              fieldName="provider_type_ids"
              provider_org_ids={Array.isArray(filters.provider_org_ids) ? filters.provider_org_ids : []}
            />
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-gray-200 bg-white">
        {isLoading && !hasResults ? (
          <div className="flex justify-center py-16">
            <ThreeDotBouncing />
          </div>
        ) : (
          <div className="p-5">
            <WeeklyClosuresList rows={reportData?.rows} />
          </div>
        )}
      </div>
    </div>
  );
};

export default WeeklyClosures;
